import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import './CatchGame.css';
import { Slider } from "@mui/material";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";
import { CatchGameProps } from "../../app/models/catchGameProps";
import { FormStateProps } from "../../app/models/formStateProps";
import { ModalConfig } from "../../app/models/modalConfig";
import agent from "../../app/api/agent";
import { Player } from "../../app/models/player";
import UserDataModal, { UserDataValues } from "../UserDataModal";

type Falling = {
  id: number;
  x: number;     // left px
  y: number;     // top px
  size: number;  // px
  vy: number;    // px/s
};

interface CatchGameInnerProps {
  isActive?: boolean;
  collectUserData: number;
  catchGame: CatchGameProps['catchGame'];
  formState: FormStateProps['formState'];
  modalConfig: ModalConfig;
  campaignId: string;
  campaignTitle: string;
  textColor: string;
  width?: number;
}

type Step = "game" | "scoreboard";

interface ScoreboardEntry {
  rank: number;
  name: string;
  points: number;
  isActive: boolean;
}

interface ScoreboardDto {
  total: number;
  rank: number;
  entries: ScoreboardEntry[];
}

const CatchGame: React.FC<CatchGameInnerProps> = ({
  isActive,
  collectUserData,
  catchGame,
  formState,
  modalConfig,
  campaignId,
  campaignTitle,
  textColor,
  width
}) => {
  // Layout
  const CATCHER_W = 100;
  const CATCHER_H = 100;
  const OBJ_SIZE = 36;

  // ----- Step & basic game state -----
  const [step, setStep] = useState<Step>("game");

  // Measure container width when width prop is NOT provided
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    if (width) return; // if a fixed width is given, no need to measure

    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    measure(); // initial
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [width]);

  // Final stage width: explicit width prop > measured container > fallback
  const stageW = width ?? containerWidth ?? 540;
  const stageH = catchGame.gameHeight;
  const floorY = stageH - catchGame.floorHeight;
  const catcherY = floorY - CATCHER_H;

  // State
  const [catcherX, setCatcherX] = useState<number>(stageW / 2 - CATCHER_W / 2);
  const catcherXRef = useRef(catcherX);
  useEffect(() => {
    catcherXRef.current = catcherX;
  }, [catcherX]);

  // Re-clamp & center catcher whenever stageW changes
  useEffect(() => {
    const safeWidth = Math.max(stageW, CATCHER_W);
    setCatcherX(prev =>
      Math.min(Math.max(0, prev || 0), safeWidth - CATCHER_W) || safeWidth / 2 - CATCHER_W / 2
    );
  }, [stageW]);

  const [falling, setFalling] = useState<Falling[]>([]);
  const [nextId, setNextId] = useState(1);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState<number>(catchGame.gameDuration);
  const [running, setRunning] = useState<boolean>(collectUserData === 2 ? false : true);

  // Refs for RAF & timers
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const spawnTimerRef = useRef<number | null>(null);
  const gameEndedRef = useRef(false);

  // ----- User data / player handling -----
  const [player, setPlayer] = useState<Player | null>(null);
  const [dataCollected, setDataCollected] = useState<boolean>(
    collectUserData !== 2
  );
  const [openModal, setOpenModal] = useState<boolean>(collectUserData === 2);

  // For collectUserData = 3, we need to know last gameId
  const [currentGameId, setCurrentGameId] = useState<string | null>(null);

  // Scoreboard state
  const [scoreboard, setScoreboard] = useState<ScoreboardDto | null>(null);

  // Prepare initial form values for UserDataModal
  const initialFormValues: any = useMemo(
    () => ({
      firstName: player?.firstName || "",
      lastName: player?.lastName || "",
      email: player?.email || "",
      phone: player?.phone || "",
      company: player?.company || "",
      gender: player?.gender ?? undefined,
      bonusField: player?.bonusField || "",
      bonusFieldLabel: formState?.bonusField || "",
      formAddPermissionChecked: player?.formAddPermissionChecked || false,
      formAddPermission2Checked: player?.formAddPermission2Checked || false,
    }),
    [player, formState]
  );

  // Phase speeds (px/s) & spawn intervals per phase
  const phase = useMemo<1 | 2 | 3>(() => {
    if (!running) return 1;
    const third = catchGame.gameDuration / 3;
    if (timeLeft > 2 * third) return 1;
    if (timeLeft > third) return 2;
    return 3;
  }, [timeLeft, running, catchGame.gameDuration]);

  const fallSpeed = phase === 1 ? 170 : phase === 2 ? 260 : 360; // px/s
  const spawnEveryMs = Math.max(
    phase === 1 ? 1100 : phase === 2 ? 800 : 550,
    catchGame.minSpawnGap
  );

  // Spawn logic
  const spawn = useCallback(() => {
    setFalling(prev => [
      ...prev,
      {
        id: nextId,
        x: Math.random() * (stageW - OBJ_SIZE),
        y: -OBJ_SIZE,
        size: OBJ_SIZE,
        vy: fallSpeed,
      },
    ]);
    setNextId(id => id + 1);
  }, [nextId, stageW, fallSpeed]);

  // Spawn timer per phase
  useEffect(() => {
    if (!running || step !== "game") return;
    if (spawnTimerRef.current) window.clearInterval(spawnTimerRef.current);
    spawnTimerRef.current = window.setInterval(spawn, spawnEveryMs) as unknown as number;
    return () => {
      if (spawnTimerRef.current) window.clearInterval(spawnTimerRef.current);
    };
  }, [spawnEveryMs, spawn, running, step]);

  // ----- Game finished handler -----
  const handleGameFinished = useCallback(async () => {
    if (gameEndedRef.current) return;
    gameEndedRef.current = true;

    const nowScore = score;
    const now = new Date().toISOString();

    // Decide how we handle player
    let effectivePlayer = player;
    let playerId = player?.id;

    // For collectUserData 1 & 3, we create a fresh player if none exists
    if (!effectivePlayer) {
      playerId = uuid();
      effectivePlayer = {
        id: playerId,
        campaignId,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        gender: 0,
        bonusField: "",
        bonusFieldLabel: formState?.bonusField || "",
        formAddPermissionChecked: false,
        formAddPermission2Checked: false,
      };
      // create player immediately
      try {
        await agent.Players.create(effectivePlayer);
      } catch (err) {
        console.error("Player create error", err);
      }
      setPlayer(effectivePlayer);
    }

    if (!playerId || !effectivePlayer) return;

    const gameId = uuid();

    const gamePayload = {
      id: gameId,
      campaignId,
      playerId,
      // gameType for Catch Game – adjust if your backend uses a different value
      gameType: 7,
      prize: `${nowScore} Puan`,
      isPrize: true,
      hasWon: nowScore > 0,
      datePlayed: now,
      points: nowScore,
      timeCompleted: null as number | null,
      player: effectivePlayer,
    };

    // Use modalConfig for styling the finish modal
    const {
      modalText,
      modalPassText,
      modalBg,
      modalInnerBg,
      modalColor,
      modalTextColor,
      modalBorderRadius,
    } = modalConfig || {};

    const titleText =
      nowScore > 0
        ? modalText || "Tebrikler!"
        : modalPassText || "Katıldığınız için teşekkürler";

    await Swal.fire({
      title: titleText,
      html: `<div style="font-size: 22px; margin-top: 10px;">Skorunuz: <strong>${nowScore}</strong> Puan</div>`,
      icon: nowScore > 0 ? "success" : "info",
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: modalInnerBg || undefined,
      color: modalTextColor || undefined,
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          if (modalColor) {
            popup.style.backgroundColor = modalColor;
          }
          if (modalBorderRadius) {
            popup.style.borderRadius = `${modalBorderRadius}px`;
          }
        }
        if (modalBg) {
          const container = Swal.getContainer();
          if (container) {
            container.style.backgroundImage = `url(${modalBg})`;
            container.style.backgroundSize = "cover";
            container.style.backgroundPosition = "center";
          }
        }
        // close when user clicks anywhere
        popup?.addEventListener(
          "click",
          () => {
            Swal.close();
          },
          { once: true }
        );
      },
    });

    // Create game record
    try {
      await agent.Games.create(gamePayload as any);
    } catch (err) {
      console.error("Game create error", err);
    }

    setCurrentGameId(gameId);

    // collectUserData === 3 → show form after game, THEN scoreboard
    if (collectUserData === 3) {
      setOpenModal(true);
      return;
    }

    // Otherwise go directly to scoreboard
    await goScoreboard(gameId);
  }, [
    score,
    player,
    campaignId,
    formState,
    modalConfig,
    collectUserData,
  ]);

  // RAF Loop
  useEffect(() => {
    if (!running || step !== "game") return;

    const tick = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000; // seconds
      lastTsRef.current = ts;

      // Move falling
      setFalling(prev => {
        const arr: Falling[] = [];
        for (const f of prev) {
          const ny = f.y + f.vy * dt;
          const cx = catcherXRef.current; // latest catcher X

          // Collision AABB with catcher
          const intersects =
            ny + f.size >= catcherY &&
            f.x < cx + CATCHER_W &&
            f.x + f.size > cx &&
            ny <= catcherY + CATCHER_H;

          if (intersects) {
            setScore(s => s + catchGame.pointMultiplier);
            continue; // caught, so remove
          }
          // Despawn if below floor
          if (ny > stageH) continue;

          arr.push({ ...f, y: ny });
        }
        return arr;
      });

      // Timer
      setTimeLeft(t => {
        const nt = Math.max(0, t - dt);
        if (nt === 0 && !gameEndedRef.current) {
          setRunning(false);
          handleGameFinished();
        }
        return nt;
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
    };
  }, [running, stageH, catchGame.pointMultiplier, catcherY, step, handleGameFinished]);

  // Keyboard control
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!running || step !== "game") return;
      const stepX = 24; // px per keydown
      if (e.key === "ArrowLeft") {
        setCatcherX(x => Math.max(0, x - stepX));
      } else if (e.key === "ArrowRight") {
        setCatcherX(x => Math.min(stageW - CATCHER_W, x + stepX));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running, stageW, step]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (spawnTimerRef.current) window.clearInterval(spawnTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current!);
    };
  }, []);

  // Helpers
  const mmss = (secs: number) => {
    const s = Math.max(0, Math.floor(secs));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(1, "0")}:${String(r).padStart(2, "0")}`;
  };

  const sliderValue =
    stageW > CATCHER_W ? (catcherX / (stageW - CATCHER_W)) * 100 : 0;

  // ----- Scoreboard fetch -----
  const goScoreboard = useCallback(
    async (gameId?: string) => {
      const gid = gameId || currentGameId;
      if (!gid) {
        setStep("scoreboard");
        return;
      }

      try {
        const res = await agent.Games.scoreboard(campaignId, gid);
        // No reverse here: higher points = better rank
        setScoreboard(res);
      } catch (err) {
        console.error("Scoreboard error", err);
        setScoreboard(null);
      }
      setStep("scoreboard");
    },
    [campaignId, currentGameId]
  );

  // ----- Reset game (used when clicking scoreboard page) -----
  const resetGame = useCallback(() => {
    // reset game state
    setFalling([]);
    setNextId(1);
    setScore(0);
    setTimeLeft(catchGame.gameDuration);
    setScoreboard(null);
    setCurrentGameId(null);
    gameEndedRef.current = false;
    lastTsRef.current = null;

    if (collectUserData === 2) {
      setPlayer(null);
      setDataCollected(false);
      setOpenModal(true);
      setRunning(false);
    } else {
      setRunning(true);
    }

    setStep("game");
  }, [catchGame.gameDuration, collectUserData]);

  // ----- UserDataModal handlers -----
  const handleSubmit = async (values: UserDataValues) => {
    const id = player?.id || uuid();
    const updated: Player = {
      id,
      campaignId,
      firstName: values.firstName || "",
      lastName: values.lastName || "",
      email: formState.formEmail ? values.email || "" : "",
      phone: formState.formPhone ? values.phone || "" : "",
      company: formState.formCompany ? values.company || "" : "",
      gender: formState.formGender ? (values.gender === 1 ? 1 : 2) : 0,
      formAddPermissionChecked: formState.formAddPermission ? values.formAddPermissionChecked || false : false,
      formAddPermission2Checked: formState.formAddPermission2 ? values.formAddPermission2Checked || false : false,
      bonusField: formState.bonusField ? values[formState.bonusField] || "" : "",
      bonusFieldLabel: collectUserData != 1 ? formState.bonusField || "" : "",
    };

    try {
      if (player) {
        await agent.Players.update(updated);
      } else {
        await agent.Players.create(updated);
      }
      setPlayer(updated);
      setDataCollected(true);
      setOpenModal(false);

      // If form is AFTER game (collectUserData = 3), we go to scoreboard now
      if (collectUserData === 3 && currentGameId) {
        await goScoreboard(currentGameId);
      } else if (collectUserData === 2 && !running) {
        // Start the game after collecting data
        setRunning(true);
      }
    } catch (err) {
      console.error("UserData submit error", err);
    }
  };

  const handleCloseModal = () => {
    // For before-game mode, don't allow closing without submit
    if (collectUserData === 2 && !dataCollected) return;
    setOpenModal(false);
  };

  // ----- isActive lock -----
  const inactiveShownRef = useRef(false);
  useEffect(() => {
    if (isActive === false && !inactiveShownRef.current) {
      inactiveShownRef.current = true;
      Swal.fire({
        title: "Oyun şu anda aktif değil",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    }
  }, [isActive]);

  if (isActive === false) {
    return (
      <div
        style={{
          width: width ? `${width}px` : "100%",
          textAlign: "center",
          color: "#fff",
          padding: "40px 0",
        }}
      >
        Bu oyun kampanya için pasif durumda.
      </div>
    );
  }

   // Scoreboard step: click anywhere to restart
  if (step === "scoreboard") {
    return (
      <>
        <div
          className="step-box scoreboard catchgame"
          style={{ cursor: "pointer" }}
          onClick={resetGame}
        >
          <div className="scoreboard-inner">
            <h2 className="score-title">Sıralama</h2>
            <div className="scoreboard-container no-scrollbar">
              {scoreboard?.entries?.length ? (
                scoreboard.entries.map((e) => (
                  <div
                    key={e.rank}
                    className={`score-row ${e.isActive ? "active" : ""}`}
                  >
                    <span className="rank">{e.rank}.</span>
                    <span className="name">
                      {e.name || "-"}
                    </span>
                    <span className="pts">
                      {e.points ?? 0} Puan
                    </span>
                  </div>
                ))
              ) : (
                <div className="score-row">
                  <span className="scoreboard-name">
                    Henüz skor bulunmuyor.
                  </span>
                </div>
              )}
            </div>
            <p className="scoreboard-hint">
              Yeni oyun başlatmak için ekrana dokunun / tıklayın.
            </p>
          </div>
        </div>

        <UserDataModal
          open={openModal}
          onClose={handleCloseModal}
          formState={formState}
          initialValues={initialFormValues}
          onSubmit={async (values) => {
            await handleSubmit(values);
            return true;      // ✔ satisfies expected type
          }}
          submitLabel="Gönder"
        />
      </>
    );
  }

  return (
    <div
      ref={containerRef}
      id="catchGame"
      style={{
        width: width ? `${width}px` : "100%",
      }}
    >
      <div className="catch-inner" style={{height: stageH}}>
        {/* HUD */}
        <div className="hud left" style={{color: textColor}}>Puan:{score}</div>
        <div className="hud right" style={{color: textColor}}>{mmss(timeLeft)}</div>

        {/* Line hint */}
        <div className="line-hint" />

        {/* Falling objects */}
        {falling.map(obj => (
          <div
            key={obj.id}
            style={{
              position: "absolute",
              left: obj.x,
              top: obj.y,
              width: obj.size,
              height: obj.size,
              borderRadius: 6,
              background: catchGame.catchObject ? `url(${catchGame.catchObject})` : "#ff8fa3",
              backgroundSize: obj.size + 'px ' + obj.size + 'px',
              boxShadow: catchGame.catchObject ? "none" : "0 4px 10px rgba(0,0,0,.4)",
            }}
          />
        ))}

        {/* Catcher */}
        <div
          style={{
            position: "absolute",
            left: catcherX,
            top: catcherY,
            width: CATCHER_W,
            height: CATCHER_H,
            borderRadius: 10,
            background: catchGame.catcher ? `url(${catchGame.catcher})` : "linear-gradient(180deg,#7dd3fc,#0369a1)",
            backgroundSize: CATCHER_W + 'px ' + CATCHER_H + 'px',
            boxShadow: catchGame.catcher ? "none" : "0 8px 18px rgba(0,0,0,.5) inset, 0 8px 20px rgba(0,0,0,.35)",
          }}
        />

        {/* Floor */}
        <div
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "100%",
            height: catchGame.floorHeight,
            background: catchGame.floor ? `url(${catchGame.floor})` : "linear-gradient(180deg,#c49a6c,#8b5e34)",
          }}
        />

        {/* Slider on top of floor */}
        <div
          style={{
            position: "absolute",
            left: 24,
            right: 24,
            bottom: catchGame.floorHeight / 2 - 12, // roughly centered over floor
            zIndex: 6,
          }}
        >
          <Slider
            aria-label="Catcher"
            value={sliderValue}
            onChange={(_, v) => {
              const pct = Array.isArray(v) ? v[0] : v;
              const range = stageW - CATCHER_W;
              const x = range > 0 ? ((pct as number) / 100) * range : 0;
              setCatcherX(x);
            }}
            sx={{
              color: catchGame.sliderColor,
              height: 8,
              "& .MuiSlider-rail": {
                height: 8,
                opacity: 0.4,
              },
              "& .MuiSlider-track": {
                height: 8,
              },
              "& .MuiSlider-thumb": {
                width: 40,
                height: 40,
                boxShadow: "0 0 0 4px rgba(0,0,0,.25)",
              },
            }}
          />
        </div>
      </div>

      <UserDataModal
        open={openModal}
        onClose={handleCloseModal}
        formState={formState}
        initialValues={initialFormValues}
        onSubmit={async (values) => {
          await handleSubmit(values);
          return true;      // ✔ satisfies expected type
        }}
        submitLabel="Gönder"
      />
    </div>
  )
};

export default CatchGame;